import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  FileText,
  CheckCircle2,
  Clock,
  TrendingUp,
  Shield,
  Fingerprint,
  PenTool,
  Loader2
} from 'lucide-react';

const COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];

const DashboardAnalytics = () => {
  const { api } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/analytics/dashboard');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-white font-medium">{label || payload[0].name}</p>
          <p className="text-blue-400">{payload[0].value} contratos</p>
        </div>
      );
    }
    return null;
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-400">Cargando estadísticas...</span>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const { 
    total_contracts, 
    signed_contracts, 
    draft_contracts, 
    contracts_by_type,
    contracts_by_month,
    average_risk_score,
    signature_types
  } = analytics;

  // Skip if no data
  if (total_contracts === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border bg-gradient-to-r from-blue-500/5 to-purple-500/5">
        <h2 className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          Tus Estadísticas
        </h2>
        <p className="text-sm text-slate-400 mt-1">Resumen de tu actividad legal</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 md:p-6">
        <div className="bg-slate-800/50 rounded-xl p-4 flex flex-col items-center justify-center min-h-[120px]">
          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center mb-2">
            <FileText className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-white">{total_contracts}</div>
          <div className="text-xs text-slate-400 text-center">Total Contratos</div>
        </div>

        <div className="bg-slate-800/50 rounded-xl p-4 flex flex-col items-center justify-center min-h-[120px]">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-400">{signed_contracts}</div>
          <div className="text-xs text-slate-400 text-center">Firmados</div>
        </div>

        <div className="bg-slate-800/50 rounded-xl p-4 flex flex-col items-center justify-center min-h-[120px]">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center mb-2">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-400">{draft_contracts}</div>
          <div className="text-xs text-slate-400 text-center">Borradores</div>
        </div>

        <div className="bg-slate-800/50 rounded-xl p-4 flex flex-col items-center justify-center min-h-[120px]">
          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center mb-2">
            <Shield className="w-5 h-5 text-purple-400" />
          </div>
          <div className={`text-2xl font-bold ${
            average_risk_score === null ? 'text-slate-500' :
            average_risk_score < 30 ? 'text-emerald-400' :
            average_risk_score < 60 ? 'text-amber-400' : 'text-rose-400'
          }`}>
            {average_risk_score !== null ? average_risk_score : '-'}
          </div>
          <div className="text-xs text-slate-400 text-center">Riesgo Promedio</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 md:p-6 pt-0">
        {/* Contracts by Month */}
        {contracts_by_month.length > 0 && (
          <div className="bg-slate-800/30 rounded-xl p-4 flex flex-col">
            <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              Contratos por Mes
            </h3>
            <div className="h-48 flex-1 min-h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={contracts_by_month} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={{ stroke: '#475569' }}
                    tickLine={{ stroke: '#475569' }}
                  />
                  <YAxis 
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={{ stroke: '#475569' }}
                    tickLine={{ stroke: '#475569' }}
                    allowDecimals={false}
                    width={30}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="contracts" 
                    fill="#3b82f6" 
                    radius={[4, 4, 0, 0]}
                    name="Contratos"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Contracts by Type */}
        {contracts_by_type.length > 0 && (
          <div className="bg-slate-800/30 rounded-xl p-4 flex flex-col">
            <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-400" />
              Tipos de Contratos
            </h3>
            <div className="h-48 flex-1 min-h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <Pie
                    data={contracts_by_type}
                    dataKey="value"
                    nameKey="name"
                    cx="35%"
                    cy="50%"
                    outerRadius={55}
                    innerRadius={25}
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                  >
                    {contracts_by_type.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        opacity={activeIndex === null || activeIndex === index ? 1 : 0.5}
                        style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: '10px', paddingLeft: '10px' }}
                    formatter={(value) => <span className="text-slate-300">{value.length > 12 ? value.slice(0, 12) + '...' : value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Signature Types */}
      {(signature_types.manual > 0 || signature_types.biometric > 0) && (
        <div className="px-4 md:px-6 pb-4 md:pb-6">
          <div className="bg-slate-800/30 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Métodos de Firma Usados</h3>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <PenTool className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-white">{signature_types.manual}</div>
                  <div className="text-xs text-slate-400">Manual</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Fingerprint className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-white">{signature_types.biometric}</div>
                  <div className="text-xs text-slate-400">Biométrica</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAnalytics;
